import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Download, LogOut, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND_NAME } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wa_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [fetching, setFetching] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/admin/waitlist`, authHeader());
      setEntries(data.entries || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load the waitlist.");
      }
    } finally {
      setFetching(false);
    }
  }, [token, authHeader]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEntries([]);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "waitlist.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-sage flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-paperwhite rounded-3xl border border-ink/5 shadow-lg p-8 md:p-10">
          <div className="w-12 h-12 rounded-2xl bg-green/10 flex items-center justify-center mb-6">
            <Lock className="w-6 h-6 text-green-dark" strokeWidth={1.75} />
          </div>
          <h1 className="font-serif text-3xl text-ink mb-2">{BRAND_NAME} admin</h1>
          <p className="text-ink/60 mb-8">Sign in to view the waitlist.</p>
          <form onSubmit={login} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="admin-email"
                value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                className="h-12 rounded-xl" placeholder="admin@wordamp.app" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="admin-password"
                value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                className="h-12 rounded-xl" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-3.5 font-semibold text-sage hover:bg-green-dark transition-colors duration-300 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="font-serif text-4xl text-ink">Waitlist</h1>
            <p className="text-ink/60 mt-1" data-testid="admin-count">{entries.length} {entries.length === 1 ? "signup" : "signups"}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} data-testid="admin-export-btn"
              className="inline-flex items-center gap-2 rounded-full bg-green px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-dark transition-colors duration-300">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink hover:bg-paperwhite transition-colors duration-300">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {entries.length > 0 && (
          <div className="mb-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-insights">
            {Object.entries(
              entries.reduce((acc, e) => { acc[e.segment] = (acc[e.segment] || 0) + 1; return acc; }, {})
            )
              .sort((a, b) => b[1] - a[1])
              .map(([seg, n]) => (
                <div
                  key={seg}
                  data-testid={`insight-${seg.toLowerCase().replace(/\s/g, "-")}`}
                  className="bg-paperwhite rounded-2xl border border-ink/5 px-5 py-4 shadow-sm"
                >
                  <div className="font-serif text-3xl text-ink leading-none">{n}</div>
                  <div className="text-xs text-ink/60 mt-1.5">{seg}</div>
                </div>
              ))}
          </div>
        )}

        <div className="bg-paperwhite rounded-3xl border border-ink/5 shadow-sm overflow-hidden">
          {fetching ? (
            <div className="p-16 text-center text-ink/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : entries.length === 0 ? (
            <div className="p-16 text-center text-ink/50" data-testid="admin-empty">No signups yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-ink/10 text-xs uppercase tracking-wider text-ink/50">
                    <th className="px-6 py-4 font-semibold">Name</th>
                    <th className="px-6 py-4 font-semibold">Email</th>
                    <th className="px-6 py-4 font-semibold">Segment</th>
                    <th className="px-6 py-4 font-semibold">Note</th>
                    <th className="px-6 py-4 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.id || i} className="border-b border-ink/5 last:border-0 hover:bg-sage/40 transition-colors" data-testid={`admin-row-${i}`}>
                      <td className="px-6 py-4 font-medium text-ink">{e.name}</td>
                      <td className="px-6 py-4 text-ink/70">{e.email}</td>
                      <td className="px-6 py-4"><span className="rounded-full bg-green/10 text-green-dark text-xs font-medium px-3 py-1">{e.segment}</span></td>
                      <td className="px-6 py-4 text-ink/60 max-w-xs truncate">{e.note || "—"}</td>
                      <td className="px-6 py-4 text-ink/50 text-sm whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
